/**
 * Activity Status Utility
 * Calculates user activity status based on last login time
 * Used in chat headers to display "현재 활동 중" or "X분/시간/일 전 활동"
 */

export interface ActivityStatus {
  isActive: boolean;
  statusText: string;
}

/**
 * Get activity status based on last login timestamp
 * @param lastLoginAt - ISO timestamp of last login (from users.last_login_at)
 * @returns Activity status with isActive flag and display text
 *
 * Status logic:
 * - < 10 min: "방금 전 활동" (NOT active - real-time status uses Presence)
 * - 10-60 min: "X분 전 활동"
 * - 1-24 hours: "X시간 전 활동"
 * - > 24 hours: "X일 전 활동"
 *
 * NOTE: "현재 활동 중" is only shown when user is actually online via Supabase Presence.
 * This function is for OFFLINE users only.
 */
export function getActivityStatus(
  lastLoginAt: string | null | undefined,
): ActivityStatus {
  if (!lastLoginAt) {
    return { isActive: false, statusText: "" };
  }

  const lastLogin = new Date(lastLoginAt);
  const now = new Date();
  const diffMinutes = Math.floor(
    (now.getTime() - lastLogin.getTime()) / (1000 * 60),
  );

  // 10분 미만이어도 isActive: false - 실시간 접속 상태는 Presence로만 판단
  if (diffMinutes < 10) {
    return { isActive: false, statusText: "방금 전 활동" };
  } else if (diffMinutes < 60) {
    return { isActive: false, statusText: `${diffMinutes}분 전 활동` };
  } else if (diffMinutes < 1440) {
    const hours = Math.floor(diffMinutes / 60);
    return { isActive: false, statusText: `${hours}시간 전 활동` };
  } else {
    const days = Math.floor(diffMinutes / 1440);
    return { isActive: false, statusText: `${days}일 전 활동` };
  }
}
